import { createCustomFieldStatus, updateCustomFieldStatus, deleteCustomFieldStatus } from "../api/cf_statuses";
import { getHidedChecklistsBySettings } from "../api/checklists";
import { useChecklistsState } from "../state/checklistsState";
import { getCurrLeadId } from "../amo/leads";
import { getCurrentLeadStatus } from "../amo/statuses";
import { notification } from "antd";
import { syncCustomFieldToAmo, computeBackfillForCustomField } from "./valueSyncService";
import { setAmoFieldValue, clearMultiselectField, getAllAmoFields, getCurrentAmoFieldValue } from "../utils/amoFieldsUtils";
import { getCustomFieldDataId } from "../amo/customFields";
import {
    buildAmoPrerequisiteValuesMap,
    canApplyChecklistFieldValue,
    getMissingAmoPrerequisiteFieldsForCustomField,
} from "../utils/checklistAmoPrerequisites";

const getAmoFieldNamesById = () => {
    return getAllAmoFields().reduce((acc, field) => {
        acc[String(field.id)] = field.name;
        return acc;
    }, {});
};

/**
 * Проверяет, можно ли сохранить новое значение поля чеклиста при AMO-блокировках.
 *
 * Guard находится перед optimistic update и API-запросом, чтобы пользователь не
 * видел кратковременное ложное заполнение и чтобы backend не получал значение,
 * которое запрещено текущими настройками чеклиста.
 */
function ensureCustomFieldCanBeUpdated(checklistId, customFieldId, nextContent) {
    const { checklists } = useChecklistsState.getState();
    const checklist = checklists.find(c => c.id === checklistId);
    if (!checklist) {
        return true;
    }

    const amoValuesByFieldId = buildAmoPrerequisiteValuesMap(
        [checklist],
        getCurrentAmoFieldValue
    );
    if (
        canApplyChecklistFieldValue(
            checklist,
            customFieldId,
            nextContent,
            amoValuesByFieldId
        )
    ) {
        return true;
    }

    const missingFieldIds = getMissingAmoPrerequisiteFieldsForCustomField(
        checklist,
        customFieldId,
        amoValuesByFieldId
    );
    const namesById = getAmoFieldNamesById();
    const missingFieldNames = missingFieldIds.map(
        fieldId => namesById[String(fieldId)] || `AMO поле ${fieldId}`
    );
    notification.warning({
        message: 'Заполните AMO-поля',
        description: `Заполните AMO-поля: ${missingFieldNames.join(', ')}`,
        placement: 'bottomRight'
    });
    return false;
}



export async function toggleCustomField(checklistId, cf, toggle) {
    const { updateCustomField, setHiddenChecklists } = useChecklistsState.getState();
    const nextContent = { active: toggle };
    if (!ensureCustomFieldCanBeUpdated(checklistId, cf.id, nextContent)) {
        return;
    }
    const prevStatus = cf.content?.active || false;
    const prevStatusId = cf.statusId;

    updateCustomField(checklistId, cf.id, { content: nextContent });
    try {
        const statusData = { content: nextContent };
        let newStatusId = cf.statusId;
        if (cf.statusId) {
            await updateCustomFieldStatus(cf.statusId, statusData);
        } else {
            const newStatus = await createCustomFieldStatus({ custom_field: cf.id, lead_id: getCurrLeadId(), ...statusData });
            newStatusId = newStatus.id;
            updateCustomField(checklistId, cf.id, { statusId: newStatusId });
        }
        const data = await getHidedChecklistsBySettings(getCurrLeadId(), getCurrentLeadStatus());
        setHiddenChecklists(data);
    } catch (error) {
        updateCustomField(checklistId, cf.id, { content: { active: prevStatus }, statusId: prevStatusId });
        notification.error({ message: 'Ошибка', description: 'Не удалось обновить статус поля', placement: 'bottomRight' });
    }
}

export async function selectCustomFieldValue(checklistId, cf, value) {
    const { updateCustomField, setHiddenChecklists } = useChecklistsState.getState();
    const prevValue = cf.content?.value || null;
    const prevStatusId = cf.statusId;

    // Приводим пустую строку к null для унификации
    const normalizedValue = value === '' ? null : value;
    const nextContent = { value: normalizedValue };
    if (!ensureCustomFieldCanBeUpdated(checklistId, cf.id, nextContent)) {
        return;
    }

    updateCustomField(checklistId, cf.id, { content: nextContent });
    try {
        
        const apiValue = normalizedValue === null || normalizedValue === undefined ? '' : normalizedValue;
        const statusData = { content: { value: apiValue } };
        let newStatusId = cf.statusId;
        if (cf.statusId) {
            await updateCustomFieldStatus(cf.statusId, statusData);
        } else {
            const newStatus = await createCustomFieldStatus({ custom_field: cf.id, lead_id: getCurrLeadId(), ...statusData });
            newStatusId = newStatus.id;
            updateCustomField(checklistId, cf.id, { statusId: newStatusId });
        }
        
        
        
        try {
            const syncResult = await syncCustomFieldToAmo(cf, normalizedValue, true);
            if (!syncResult.success && syncResult.error !== 'Нет сопоставления с AMO полем') {
                notification.warning({
                    message: 'Проблема с синхронизацией',
                    description: syncResult.error,
                });
            }
        } catch (syncError) {
            console.error('Ошибка синхронизации с AMO:', syncError);
        }
        
        const data = await getHidedChecklistsBySettings(getCurrLeadId(), getCurrentLeadStatus());
        setHiddenChecklists(data);
    } catch (error) {
        updateCustomField(checklistId, cf.id, { content: { value: prevValue }, statusId: prevStatusId });
        notification.error({ message: 'Ошибка', description: 'Не удалось сохранить значение поля', placement: 'bottomRight' });
    }
}

export async function multiselectCustomFieldValues(checklistId, cf, values) {
    const { updateCustomField, setHiddenChecklists } = useChecklistsState.getState();
    const prevValues = cf.content?.values || [];
    const prevStatusId = cf.statusId;

    // Валидация входящих значений - гарантируем что values всегда массив
    const validatedValues = Array.isArray(values) ? values : (values ? [values] : []);
    const nextContent = { values: validatedValues };
    if (!ensureCustomFieldCanBeUpdated(checklistId, cf.id, nextContent)) {
        return;
    }
    
    updateCustomField(checklistId, cf.id, { content: nextContent });
    try {
        const statusData = { content: nextContent };
        let newStatusId = cf.statusId;
        if (cf.statusId) {
            await updateCustomFieldStatus(cf.statusId, statusData);
        } else {
            const newStatus = await createCustomFieldStatus({ custom_field: cf.id, lead_id: getCurrLeadId(), ...statusData });
            newStatusId = newStatus.id;
            updateCustomField(checklistId, cf.id, { statusId: newStatusId });
        }
        
        // Синхронизация с AMO полями
        try {
            const syncResult = await syncCustomFieldToAmo(cf, validatedValues, true);
            if (!syncResult.success && syncResult.error !== 'Нет сопоставления с AMO полем') {
                notification.warning({
                    message: 'Проблема с синхронизацией',
                    description: syncResult.error,
                });
            }
        } catch (syncError) {
            console.error('Ошибка синхронизации с AMO:', syncError);
        }
        
        const data = await getHidedChecklistsBySettings(getCurrLeadId(), getCurrentLeadStatus());
        setHiddenChecklists(data);
    } catch (error) {
        updateCustomField(checklistId, cf.id, { content: { values: prevValues }, statusId: prevStatusId });
        notification.error({ message: 'Ошибка', description: 'Не удалось сохранить значения поля', placement: 'bottomRight' });
    }
}

/**
 * Автозаполнение пустого кастомного поля по значению AMO (обратная синхронизация)
 * Тихий режим: без уведомлений пользователю
 */
export async function backfillCustomFieldFromAmo(checklistId, cf) {
    const { updateCustomField } = useChecklistsState.getState();
    try {
        const proposal = computeBackfillForCustomField(cf);
        if (!proposal.shouldBackfill || !proposal.contentUpdate) {
            return false;
        }

        const content = proposal.contentUpdate;
        updateCustomField(checklistId, cf.id, { content });

        const statusData = { content };
        if (cf.statusId) {
            await updateCustomFieldStatus(cf.statusId, statusData);
        } else {
            const created = await createCustomFieldStatus({ custom_field: cf.id, lead_id: getCurrLeadId(), ...statusData });
            if (created && (created.id !== undefined && created.id !== null)) {
                updateCustomField(checklistId, cf.id, { statusId: created.id });
            }
        }
        return true;
    } catch (error) {
        console.warn('Ошибка обратной синхронизации для поля', cf?.id, error);
        return false;
    }
}

export async function updateHiddenChecklists(leadId, statusId) {
    const { setHiddenChecklists } = useChecklistsState.getState();
    try {
        const data = await getHidedChecklistsBySettings(leadId, statusId);
        setHiddenChecklists(data);
        return data;
    } catch (error) {
        notification.error({ message: 'Ошибка', description: 'Не удалось получить список скрытых чек-листов', placement: 'bottomRight' });
        return [];
    }
}

/**
 * Очистка всех значений чеклиста (кастомные поля и AMO поля)
 * @param {number} checklistId - ID чеклиста
 */
export async function clearChecklist(checklistId) {
    const { checklists, updateCustomField, setHiddenChecklists } = useChecklistsState.getState();
    
    
    const checklist = checklists.find(c => c.id === checklistId);
    if (!checklist) {
        throw new Error('Чеклист не найден');
    }

    console.log(`Очистка чеклиста "${checklist.name}" (ID: ${checklistId})`);

    
    const getAllChildChecklists = (parentId) => {
        const children = checklists.filter(c => c.parent === parentId);
        let allChildren = [...children];
        children.forEach(child => {
            allChildren = [...allChildren, ...getAllChildChecklists(child.id)];
        });
        return allChildren;
    };

    const checklistsToProcess = [checklist, ...getAllChildChecklists(checklistId)];
    console.log('Чеклисты для очистки:', checklistsToProcess.map(c => c.name));

    const errors = [];

    
    for (const currentChecklist of checklistsToProcess) {
        console.log(`Обработка чеклиста: ${currentChecklist.name}`);

        
        if (currentChecklist.custom_fields && currentChecklist.custom_fields.length > 0) {
            for (const cf of currentChecklist.custom_fields) {
                try {
                    console.log(`Очистка кастомного поля: ${cf.name} (${cf.type})`);
                    
                    
                    let clearContent = {};
                    if (cf.type === 'C') {
                        clearContent = { active: false };
                    } else if (cf.type === 'S') {
                        clearContent = { value: '' };
                    } else if (cf.type === 'M') {
                        clearContent = { values: [] };
                    }

                    updateCustomField(currentChecklist.id, cf.id, { content: clearContent });

                    
                    if (cf.statusId) {
                        try {
                            await deleteCustomFieldStatus(cf.statusId);
                            updateCustomField(currentChecklist.id, cf.id, { statusId: null });
                            console.log(`Удален статус поля ${cf.name}`);
                        } catch (statusError) {
                            console.warn(`Не удалось удалить статус поля ${cf.name}:`, statusError);
                        }
                    }

                    
                    try {
                        let clearValue = '';
                        if (cf.type === 'M') {
                            clearValue = [];
                        }
                        
                        const syncResult = await syncCustomFieldToAmo(cf, clearValue, true);
                        if (syncResult.success) {
                            console.log(`Кастомное поле ${cf.name} синхронизировано с AMO`);
                        }
                    } catch (syncError) {
                        console.warn(`Ошибка синхронизации поля ${cf.name} с AMO:`, syncError);
                    }

                } catch (error) {
                    console.error(`Ошибка очистки кастомного поля ${cf.name}:`, error);
                    errors.push(`Поле "${cf.name}": ${error.message}`);
                }
            }
        }

        
        if (currentChecklist.fields && currentChecklist.fields.length > 0) {
            for (const field of currentChecklist.fields) {
                try {
                    const dataId = getCustomFieldDataId(field);
                    console.log(`Очистка AMO поля с ID: ${dataId}`);

                    
                    const amoFields = AMOCRM.constant("account")?.cf || {};
                    const amoField = amoFields[dataId];
                    
                    console.log(`Информация о поле ${dataId}:`, amoField);
                    
                    if (amoField) {
                        console.log(`Тип поля ${dataId}: ${amoField.TYPE_CODE}`);
                        
                        if (amoField.TYPE_CODE === 'MULTISELECT') {
                            
                            console.log(`Очистка мультиселекта ${dataId} через clearMultiselectField`);
                            const success = clearMultiselectField(dataId);
                            if (success) {
                                console.log(`✅ AMO мультиселект ${dataId} очищен`);
                            } else {
                                console.warn(`❌ Не удалось очистить AMO мультиселект ${dataId}`);
                                
                                const backupSuccess = setAmoFieldValue(dataId, []);
                                console.log(`Резервная очистка мультиселекта ${dataId}: ${backupSuccess}`);
                            }
                        } else {
                            
                            console.log(`Очистка поля ${dataId} (тип: ${amoField.TYPE_CODE}) через setAmoFieldValue`);
                            const success = setAmoFieldValue(dataId, '');
                            if (success) {
                                console.log(`✅ AMO поле ${dataId} очищено`);
                            } else {
                                console.warn(`❌ Не удалось очистить AMO поле ${dataId}`);
                            }
                        }
                    } else {
                        console.warn(`AMO поле ${dataId} не найдено в константах, пробуем универсальную очистку`);
                        
                        
                        const multiselectSuccess = clearMultiselectField(dataId);
                        if (multiselectSuccess) {
                            console.log(`✅ AMO поле ${dataId} очищено как мультиселект`);
                        } else {
                            
                            const simpleSuccess = setAmoFieldValue(dataId, '');
                            if (simpleSuccess) {
                                console.log(`✅ AMO поле ${dataId} очищено как простое поле`);
                            } else {
                                console.warn(`❌ Не удалось очистить AMO поле ${dataId} никаким способом`);
                            }
                        }
                    }

                } catch (error) {
                    console.error(`Ошибка очистки AMO поля:`, error);
                    errors.push(`AMO поле: ${error.message}`);
                }
            }
        }
    }

    
    try {
        const data = await getHidedChecklistsBySettings(getCurrLeadId(), getCurrentLeadStatus());
        setHiddenChecklists(data);
    } catch (error) {
        console.warn('Не удалось обновить список скрытых чеклистов:', error);
    }

    
    if (errors.length > 0) {
        console.warn('Ошибки при очистке чеклиста:', errors);
        throw new Error(`Некоторые поля не удалось очистить: ${errors.join(', ')}`);
    }

    console.log(`✅ Чеклист "${checklist.name}" успешно очищен`);
}
